import React, {
  CSSProperties,
  MouseEventHandler,
  useCallback,
  useMemo,
  MouseEvent,
  ReactNode,
  useState,
  createContext,
  useRef,
  MutableRefObject,
  useContext,
} from 'react'
import formatTime from 'lib/format-time'
import classNames from 'classnames'
import noopWrapper, { Wrapper } from 'lib/noop-wrapper'
import communityLink from 'Components/new/links/community-link'
import useToggleCollect, {
  IToggleCollectParams,
} from 'Components/hooks/use-toggle-collect'
import useToggleDigg, {
  IToggleDiggParams,
  makeMap,
} from 'Components/hooks/use-toggle-digg'
import formatNumber from '@arsenal/arsenal/modules/formatNumber'
import ToolTip, { IProps as ToolTipProps } from 'Components/new/tool-tip'
import ModalReport, { IReportData } from 'Components/modal-report'
import { IReportParams, report, reportUser } from 'lib/interaction'
import fetch from 'lib/decorated-fetch'
import getUrl from 'lib/url'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import Toast from 'Components/toast-messager'
import Dialog from 'Components/dialog'
import Confirm from 'Components/new/confirm'
import useLoginCheckCallback from 'Components/hooks/use-login-check-callback'
import useToggleBury, {
  IToggleBuryParams,
} from 'Components/hooks/use-toggle-bury'
import { useStatusCountUpdater } from 'Components/hooks/use-optimistic-status'
import { GlobalStoreContext } from 'Components/store'
import { unstable_batchedUpdates } from 'react-dom'
import { size14Class } from 'Components/new/community-card/text'
import dialogStyle from 'Components/dialog.module.scss'

const aid = 1839
const appName = 'auto_web_pc'

function makeShareIcon({ isHover }: { isHover?: boolean }) {
  return (
    `${
      'data:image/svg+xml;utf8,<svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="200" height="200">' +
      '    <g fill="'
    }${isHover ? '%23e65800' : '%231F2129'}">` +
    `        <path d="M405.333333 128v85.333333h-192v597.333334h597.333334v-192h85.333333v234.666666a42.666667 42.666667 0 0 1-39.466667 42.56L853.333333 896H170.666667a42.666667 42.666667 0 0 1-42.56-39.466667L128 853.333333V170.666667a42.666667 42.666667 0 0 1 39.466667-42.56L170.666667 128h234.666666z"></path>` +
    `        <path d="M823.168 140.501333l60.330667 60.330667-490.666667 490.666667-60.330667-60.330667z"></path>` +
    `        <path d="M853.333333 128a42.666667 42.666667 0 0 1 42.56 39.466667L896 170.666667v277.333333h-85.333333V213.333333H576V128h277.333333z"></path>` +
    `    </g>` +
    `</svg>`
  )
}

export const shareIcon = makeShareIcon({})
export const shareIconHover = makeShareIcon({ isHover: true })

function makeReplyIcon({ isHover }: { isHover?: boolean }) {
  return (
    `${
      'data:image/svg+xml;utf8,' +
      '<svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200">' +
      '    <path fill="'
    }${
      isHover ? '%23e65800' : '%231F2129'
    }" d="M874.666667 128a42.666667 42.666667 0 0 1 42.368 37.610667l0.298666 4.970666 1.344 597.333334a42.666667 42.666667 0 0 1-37.696 42.453333l-4.970666 0.298667H605.226667l-125.952 135.68-62.549334-58.026667 138.666667-149.333333a42.666667 42.666667 0 0 1 26.24-13.354667L586.666667 725.333333h246.570666l-1.152-512H192v512h202.666667v85.333334H149.333333a42.666667 42.666667 0 0 1-42.389333-37.696L106.666667 768V170.666667a42.666667 42.666667 0 0 1 37.696-42.389334L149.333333 128h725.333334zM384 405.333333v128h-85.333333v-128h85.333333z m341.333333 0v128h-85.333333v-128h85.333333z m-170.666666 0v128h-85.333334v-128h85.333334z"></path>` +
    `</svg>`
  )
}

export const replyIcon = makeReplyIcon({})
export const replyIconHover = makeReplyIcon({ isHover: true })

function makeCollectNormalIcon({
  theme,
  isHover,
}: {
  theme: 'light' | 'black'
  isHover?: boolean
}) {
  const fill =
    theme === 'black' ? '%23c9cbd6' : isHover ? '%23e65800' : '%231F2129'
  return (
    `${
      'data:image/svg+xml;utf8,' +
      '<svg width="20px" height="20px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' +
      '    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">' +
      '        <g transform="translate(-1690.000000, -3920.000000)" fill="'
    }${fill}">` +
    `            <g transform="translate(1690.000000, 3920.000000)">` +
    `                <path d="M9.23743323,1.64488128 C9.5639883,1.07692854 10.3664251,1.05734396 10.7257996,1.58612755 L10.7625668,1.64488128 L13.2404167,5.95458333 L18.0988526,6.98341626 C18.7403734,7.11923087 19.0064109,7.87811951 18.611819,8.38316819 L18.5701471,8.43279444 L15.2429167,12.125 L15.5381562,14.8829483 L13.788881,15.0701098 L13.4520284,11.9217696 C13.4280935,11.6980665 13.4908377,11.4747072 13.6256128,11.2968902 L13.6731855,11.2393666 L16.2416667,8.38833333 L12.4918407,7.59462326 C12.2716387,7.5480049 12.0786652,7.41901082 11.9513965,7.23562433 L11.9114599,7.17251935 L10,3.84833333 L8.08854015,7.17251935 C7.97634752,7.36764757 7.79417498,7.51149263 7.58049875,7.57607124 L7.50815931,7.59462326 L3.75791667,8.38833333 L6.32681447,11.2393666 C6.47724824,11.4063194 6.55744212,11.6237332 6.55277486,11.8465621 L6.54805605,11.9209772 L6.14291667,15.7379167 L9.64085991,14.1735554 C9.84383443,14.0827725 10.0726325,14.0726219 10.2813898,14.1431178 L10.3586448,14.1733341 L16.0100281,16.696805 L15.2927385,18.303195 L10,15.9395833 L5.46637102,17.9678554 C4.86663862,18.2360933 4.20380016,17.7768729 4.22799731,17.1347837 L4.2325089,17.0720934 L4.75708333,12.1254167 L1.42985289,8.43279444 C0.990902152,7.94564278 1.22208576,7.17542068 1.83825749,6.99904216 L1.90114739,6.98341626 L6.75916667,5.95458333 L9.23743323,1.64488128 Z"></path>` +
    `                <rect opacity="0" x="0" y="0" width="20" height="20"></rect>` +
    `            </g>` +
    `        </g>` +
    `    </g>` +
    `</svg>`
  )
}

function makeCollectActiveIcon({ theme }: { theme: 'light' | 'black' }) {
  const border =
    theme === 'light'
      ? '<g fill="%231F2129">' +
        '<path d="M9.23743323,1.64488128 C9.5639883,1.07692854 10.3664251,1.05734396 10.7257996,1.58612755 L10.7625668,1.64488128 L13.2404167,5.95458333 L18.0988526,6.98341626 C18.7403734,7.11923087 19.0064109,7.87811951 18.611819,8.38316819 L18.5701471,8.43279444 L15.2429167,12.125 L15.5381562,14.8829483 L13.788881,15.0701098 L13.4520284,11.9217696 C13.4280935,11.6980665 13.4908377,11.4747072 13.6256128,11.2968902 L13.6731855,11.2393666 L16.2416667,8.38833333 L12.4918407,7.59462326 C12.2716387,7.5480049 12.0786652,7.41901082 11.9513965,7.23562433 L11.9114599,7.17251935 L10,3.84833333 L8.08854015,7.17251935 C7.97634752,7.36764757 7.79417498,7.51149263 7.58049875,7.57607124 L7.50815931,7.59462326 L3.75791667,8.38833333 L6.32681447,11.2393666 C6.47724824,11.4063194 6.55744212,11.6237332 6.55277486,11.8465621 L6.54805605,11.9209772 L6.14291667,15.7379167 L9.64085991,14.1735554 C9.84383443,14.0827725 10.0726325,14.0726219 10.2813898,14.1431178 L10.3586448,14.1733341 L16.0100281,16.696805 L15.2927385,18.303195 L10,15.9395833 L5.46637102,17.9678554 C4.86663862,18.2360933 4.20380016,17.7768729 4.22799731,17.1347837 L4.2325089,17.0720934 L4.75708333,12.1254167 L1.42985289,8.43279444 C0.990902152,7.94564278 1.22208576,7.17542068 1.83825749,6.99904216 L1.90114739,6.98341626 L6.75916667,5.95458333 L9.23743323,1.64488128 Z"></path>' +
        '<rect opacity="0" x="0" y="0" width="20" height="20"></rect>' +
        '</g>'
      : ''
  return (
    `${
      'data:image/svg+xml;utf8,' +
      '<svg width="20px" height="20px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' +
      '    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">' +
      '        <g transform="translate(-1690.000000, -3943.000000)">' +
      '            <g transform="translate(1690.000000, 3943.000000)">' +
      '                <g fill="%23FFCC32" transform="translate(0.833333, 0.833333)">' +
      '                    <path d="M8.40409989,0.811547947 C8.73065496,0.243595205 9.53309178,0.224010627 9.89246628,0.752794215 L9.92923344,0.811547947 L12.4070833,5.12125 L17.2655193,6.15008293 C17.885656,6.28137038 18.1549245,6.9948859 17.8157533,7.49851633 L17.7784856,7.54983485 L17.7368138,7.59946111 L14.4091667,11.2920833 L14.9341578,16.2387601 C15.0012248,16.8710057 14.4022041,17.3484426 13.8169595,17.1775016 L13.7585061,17.1582279 L13.7002957,17.1345221 L9.16666667,15.10625 L4.63303768,17.1345221 C4.03330529,17.40276 3.37046683,16.9435395 3.39466398,16.3014503 L3.39917557,16.2387601 L3.92375,11.2920833 L0.59651956,7.59946111 C0.157568819,7.11230944 0.388752431,6.34208735 1.00492415,6.16570883 L1.06781406,6.15008293 L5.92583333,5.12125 L8.40409989,0.811547947 Z"></path>' +
      '                </g>' +
      '                <rect fill="%231F2129" opacity="0" x="0" y="0" width="20" height="20"></rect>'
    }${border}            </g>` +
    `        </g>` +
    `    </g>` +
    `</svg>`
  )
}

function makeDiggNormalIcon({
  type,
  isHover,
}: {
  type: 'digg' | 'bury'
  isHover?: boolean
}) {
  return (
    `data:image/svg+xml;utf8,<svg width="20px" height="20px" ${
      type === 'bury' ? 'style="transform: rotateX(180deg)"' : ''
    } viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">` +
    `    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">` +
    `        <g transform="translate(-1659.000000, -3920.000000)" fill="${
      isHover ? '%23e65800' : '%231F2129'
    }">` +
    `            <g transform="translate(1659.000000, 3920.000000)">` +
    `                <path d="M11.25,1.66666667 C11.8997468,1.66666667 12.4337072,2.16240674 12.4942779,2.79628332 L12.5,2.91666667 L12.4991667,6.66666667 L17.311444,6.66666667 C17.9611907,6.66666667 18.4951512,7.16240674 18.5557218,7.79628332 L18.561444,7.91666667 C18.561444,7.9903779 18.5549241,8.06387193 18.5419981,8.13629501 L18.5178187,8.2440197 L16.556,15.4685832 C16.1687381,16.6286042 15.1192501,17.4280666 13.9151116,17.4953922 L13.75,17.5 L9.58333333,17.5 L9.58333333,15.8333333 L13.75,15.8333333 C14.2636724,15.8333333 14.7258336,15.524721 14.9210803,15.0891511 L14.9613011,14.986464 L16.7666667,8.33333333 L11.6666667,8.33333333 C11.2393035,8.33333333 10.8870774,8.01163317 10.8389398,7.59718427 L10.8333333,7.5 L10.8325,3.33333333 L9.64530861,3.33333333 L6.96981575,7.91989252 C6.83706736,8.14746119 6.60575687,8.29713262 6.34782979,8.32758345 L6.25,8.33333333 L3.75,8.33333333 L3.74916667,15.8333333 L6.24916667,15.8333333 L6.25,11.25 L7.91666667,11.25 L7.91666667,16.6666667 C7.91666667,17.0940299 7.59496651,17.446256 7.1805176,17.4943936 L7.08333333,17.5 L2.91666667,17.5 C2.48930347,17.5 2.13707737,17.1782998 2.08893978,16.7638509 L2.08333333,16.6666667 L2.08333333,8.33333333 C2.08333333,7.4546985 2.76323149,6.7348624 3.62561471,6.67123811 L3.75,6.66666667 L5.77083333,6.66666667 L8.20567711,2.49354829 C8.48138531,2.02090567 8.96967269,1.71627218 9.50939872,1.6722022 L9.64530861,1.66666667 L11.25,1.66666667 Z"></path>` +
    `                <rect opacity="0" x="0" y="0" width="20" height="20"></rect>` +
    `            </g>` +
    `        </g>` +
    `    </g>` +
    `</svg>`
  )
}

function makeDiggActiveIcon({ type }: { type: 'digg' | 'bury' }) {
  return (
    `data:image/svg+xml;utf8,<svg width="20px" height="20px" ${
      type === 'bury' ? 'style="transform: rotateX(180deg)"' : ''
    } viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">` +
    `    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">` +
    `        <g transform="translate(-1659.000000, -3943.000000)">` +
    `            <g transform="translate(1659.000000, 3943.000000)">` +
    `                <g fill="%23FFCC32">` +
    `                    <path d="M11.25,1.66666667 C11.8972087,1.66666667 12.4295339,2.15854133 12.4935464,2.78886143 L12.5,2.91666667 L12.4991667,6.66666667 L17.311444,6.66666667 C17.9178743,6.66666667 18.4234418,7.09851135 18.5374018,7.67145578 L18.5557218,7.79628332 L18.561444,7.91666667 C18.561444,7.9903779 18.5549241,8.06387193 18.5419981,8.13629501 L18.5178187,8.2440197 L16.556,15.4685832 C16.1713786,16.620695 15.1335305,17.417148 13.93972,17.4939131 L13.75,17.5 L7.91666667,17.5 L7.91666667,11.25 L6.25,11.25 L6.25,17.5 L2.91666667,17.5 C2.49183224,17.5 2.14124835,17.1820957 2.08982618,16.7711982 L2.08333333,16.6666667 L2.08333333,8.33333333 C2.08333333,7.46130478 2.7530459,6.74569598 3.60619362,6.67278433 L3.75,6.66666667 L5.77083333,6.66666667 L8.20567711,2.49354829 C8.45381449,2.06816993 8.87414101,1.77887897 9.34918521,1.69314754 L9.50939872,1.6722022 L9.64530861,1.66666667 L11.25,1.66666667 Z"></path>` +
    `                </g>` +
    `                <rect fill="%231F2129" opacity="0" x="0" y="0" width="20" height="20"></rect>` +
    `                <g fill="%231F2129">` +
    `                    <path d="M11.25,1.66666667 C11.8997468,1.66666667 12.4337072,2.16240674 12.4942779,2.79628332 L12.5,2.91666667 L12.4991667,6.66666667 L17.311444,6.66666667 C17.9611907,6.66666667 18.4951512,7.16240674 18.5557218,7.79628332 L18.561444,7.91666667 C18.561444,7.9903779 18.5549241,8.06387193 18.5419981,8.13629501 L18.5178187,8.2440197 L16.556,15.4685832 C16.1687381,16.6286042 15.1192501,17.4280666 13.9151116,17.4953922 L13.75,17.5 L9.58333333,17.5 L9.58333333,15.8333333 L13.75,15.8333333 C14.2636724,15.8333333 14.7258336,15.524721 14.9210803,15.0891511 L14.9613011,14.986464 L16.7666667,8.33333333 L11.6666667,8.33333333 C11.2393035,8.33333333 10.8870774,8.01163317 10.8389398,7.59718427 L10.8333333,7.5 L10.8325,3.33333333 L9.64530861,3.33333333 L6.96981575,7.91989252 C6.83706736,8.14746119 6.60575687,8.29713262 6.34782979,8.32758345 L6.25,8.33333333 L3.75,8.33333333 L3.74916667,15.8333333 L6.24916667,15.8333333 L6.25,11.25 L7.91666667,11.25 L7.91666667,16.6666667 C7.91666667,17.0940299 7.59496651,17.446256 7.1805176,17.4943936 L7.08333333,17.5 L2.91666667,17.5 C2.48930347,17.5 2.13707737,17.1782998 2.08893978,16.7638509 L2.08333333,16.6666667 L2.08333333,8.33333333 C2.08333333,7.4546985 2.76323149,6.7348624 3.62561471,6.67123811 L3.75,6.66666667 L5.77083333,6.66666667 L8.20567711,2.49354829 C8.48138531,2.02090567 8.96967269,1.71627218 9.50939872,1.6722022 L9.64530861,1.66666667 L11.25,1.66666667 Z"></path>` +
    `                    <rect opacity="0" x="0" y="0" width="20" height="20"></rect>` +
    `                </g>` +
    `            </g>` +
    `        </g>` +
    `    </g>` +
    `</svg>`
  )
}

export const collectNormalIcon = makeCollectNormalIcon({ theme: 'light' })
export const collectNormalIconHover = makeCollectNormalIcon({
  theme: 'light',
  isHover: true,
})
export const collectActiveIcon = makeCollectActiveIcon({ theme: 'light' })

export const collectNormalIconBlack = makeCollectNormalIcon({ theme: 'black' })
export const collectActiveIconBlack = makeCollectActiveIcon({ theme: 'black' })

export const diggNormalIcon = makeDiggNormalIcon({ type: 'digg' })
export const diggNormalIconHover = makeDiggNormalIcon({
  type: 'digg',
  isHover: true,
})
export const diggActiveIcon = makeDiggActiveIcon({ type: 'digg' })

export const buryNormalIcon = makeDiggNormalIcon({ type: 'bury' })
export const buryActiveIcon = makeDiggActiveIcon({ type: 'bury' })

interface IButtonProps {
  icon?: string
  hoverIcon?: string
  onClick?(event: MouseEvent<HTMLButtonElement>): void
  style?: CSSProperties
  className?: string
  count?: number
  text?: string
  linkWrapper?: Wrapper
  size?: 'default' | 'small' | 'mini'
}

const iconHeightClass = 'tw-h-16 lg:tw-h-18'
const iconLeadingClass = 'tw-leading-16 lg:tw-leading-18'

export const InteractButton: React.FC<IButtonProps> = ({
  icon,
  hoverIcon,
  style,
  className,
  count,
  text,
  size,
  linkWrapper = noopWrapper,
  onClick,
}) => {
  const [iconSize, paddingLeft, fontSize, height, leading] = useMemo(() => {
    switch (size) {
      case 'mini':
        return [16, 2, 'tw-text-12', 'tw-h-18', 'tw-leading-18']
      case 'small':
        return [18, 3, 'tw-text-14', 'tw-h-20', 'tw-leading-20']
      default:
        return [20, 4, 'tw-text-16', 'tw-h-22', 'tw-leading-22']
    }
  }, [size])

  const showCount = useMemo(() => {
    return typeof count === 'number' && count > 0 ? formatNumber(count) : ''
  }, [count])

  return (
    <React.Fragment>
      <button
        type={'button'}
        className={classNames(
          className,
          size ? fontSize : size14Class,
          size ? height : iconHeightClass,
          size ? leading : iconLeadingClass,
          {
            hover: hoverIcon,
          }
        )}
        style={style}
        onClick={onClick}
      >
        {linkWrapper(
          <React.Fragment>
            <span
              className={classNames(
                'tw-inline-block tw-bg-14 lg:tw-bg-16 ',
                size ? height : iconHeightClass,
                {
                  icon,
                },
                size
                  ? ''
                  : icon
                  ? showCount || text
                    ? 'tw-pl-16 lg:tw-pl-19'
                    : 'tw-pl-14 tw-pl-16'
                  : ''
              )}
              style={{
                paddingLeft: size
                  ? icon
                    ? iconSize + (showCount || text ? paddingLeft : 0)
                    : 0
                  : undefined,
              }}
            >
              {`${showCount}${text || ''}`}
            </span>
          </React.Fragment>
        )}
      </button>
      <style jsx>
        {`
          .icon {
            background-repeat: no-repeat;
            background-position: left center;
          }
        `}
      </style>
      <style jsx>
        {`
          .icon {
            background-image: url('${icon}');
            ${size ? `background-size: ${iconSize}px ${iconSize}px;` : ''}
          }
          .hover:hover .icon {
            background-image: url('${hoverIcon || icon}');
          }
        `}
      </style>
    </React.Fragment>
  )
}

interface IDiggButtonProps
  extends IToggleDiggParams,
    Omit<IButtonProps, 'onClick'> {
  defaultIcon?: string
  text?: string
  onClick?(status: boolean): void
}

// 点赞按钮
export const DiggButton: React.FC<IDiggButtonProps> = ({
  id,
  type,
  hasDigg,
  diggCount,
  groupId,
  commentId,
  cellStyle,
  articleType,
  defaultIcon,
  onClick,
  text = '点赞',
  ...props
}) => {
  const {
    update: toggle,
    curStatus,
    curCount,
  } = useToggleDigg({
    id,
    type,
    hasDigg,
    diggCount,
    groupId,
    commentId,
    cellStyle,
    articleType,
  })

  const handleClick = stopPropagation(
    preventDefault(
      useCallback(() => {
        if (onClick) {
          onClick(curStatus)
        }
        if (toggle) {
          toggle()
        }
      }, [onClick, curStatus, toggle])
    )
  )

  return (
    <InteractButton
      icon={curStatus ? diggActiveIcon : defaultIcon || diggNormalIcon}
      hoverIcon={curStatus ? undefined : diggNormalIconHover}
      onClick={handleClick}
      text={text}
      count={curCount}
      {...props}
    />
  )
}

interface ICollectButtonProps
  extends IToggleCollectParams,
    Omit<IButtonProps, 'onClick'> {
  showCount?: boolean
  theme?: 'light' | 'black'
  renderText?(status: boolean): string
  onClick?(status: boolean): void
}

// 收藏按钮
export const CollectButton: React.FC<ICollectButtonProps> = ({
  theme = 'light',
  id,
  collected,
  collectCount,
  showCount = false,
  renderText,
  onClick,
  ...props
}) => {
  const {
    update: toggle,
    curStatus,
    curCount,
  } = useToggleCollect({
    id: id || '',
    collected,
    collectCount,
  })

  const handleClick = stopPropagation(
    preventDefault(
      useCallback(() => {
        if (onClick) {
          onClick(curStatus)
        }
        if (toggle) {
          toggle()
        }
      }, [onClick, curStatus, toggle])
    )
  )

  return (
    <InteractButton
      icon={
        curStatus
          ? theme === 'light'
            ? collectActiveIcon
            : collectActiveIconBlack
          : theme === 'light'
          ? collectNormalIcon
          : collectNormalIconBlack
      }
      hoverIcon={
        curStatus || theme === 'black' ? undefined : collectNormalIconHover
      }
      onClick={handleClick}
      text={renderText?.(curStatus) || '收藏'}
      count={showCount ? curCount : undefined}
      {...props}
    />
  )
}

export interface IBuryButtonProps
  extends IToggleBuryParams,
    Omit<IButtonProps, 'onClick'> {
  onClick?(status: boolean): void
}

export const BuryButton: React.FC<IBuryButtonProps> = ({
  id,
  buried,
  buryCount,
  articleType,
  onClick,
  ...props
}) => {
  const {
    update: toggle,
    curStatus,
    curCount,
  } = useToggleBury({
    id,
    buried,
    buryCount,
    articleType,
  })

  const handleClick = stopPropagation(
    preventDefault(
      useCallback(() => {
        if (onClick) {
          onClick(curStatus)
        }
        if (toggle) {
          toggle()
        }
      }, [onClick, curStatus, toggle])
    )
  )

  return (
    <InteractButton
      icon={curStatus ? buryActiveIcon : buryNormalIcon}
      onClick={handleClick}
      text={'反对'}
      count={curCount}
      {...props}
    />
  )
}

interface IDiggBuryContext {
  actionsRef: MutableRefObject<{
    diggStatus?: boolean
    diggBegin?: ReturnType<typeof useStatusCountUpdater>['begin']
    diggRollback?: ReturnType<typeof useStatusCountUpdater>['rollback']
    buryStatus?: boolean
    buryBegin?: ReturnType<typeof useStatusCountUpdater>['begin']
    buryRollback?: ReturnType<typeof useStatusCountUpdater>['rollback']
  }>
}

export const DiggBuryContext = createContext({} as IDiggBuryContext)

// 点赞 - 反对互斥
export const DiggBuryProvider: React.FC = ({ children }) => {
  const actionsRef: IDiggBuryContext['actionsRef'] = useRef({})
  return (
    <DiggBuryContext.Provider value={{ actionsRef }}>
      {children}
    </DiggBuryContext.Provider>
  )
}

export function stopPropagation(callback?: MouseEventHandler<HTMLElement>) {
  return useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      if (callback) {
        callback(event)
      }
    },
    [callback]
  )
}

export function preventDefault(callback?: MouseEventHandler<HTMLElement>) {
  return useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault()
      if (callback) {
        callback(event)
      }
    },
    [callback]
  )
}

interface IVerticalButtonProps {
  className?: string
  onClick?(): void
  icon?: string
  wrap?: boolean
}

// 举报 / 删除 - 更多操作里的按钮 垂直布局
const VerticalButton: React.FC<IVerticalButtonProps> = ({
  className,
  onClick,
  children,
  icon,
  wrap = true,
}) => {
  return (
    <button
      type="button"
      className={classNames(
        'tw-text-center hover:tw-text-common-orange',
        className
      )}
      onClick={onClick}
    >
      <i className={classNames('DCD_Icon tw-text-18', icon)} />
      {wrap ? <br /> : ''}
      <span className={wrap ? '' : 'tw-ml-4'}>{children}</span>
    </button>
  )
}

interface IReportButtonProps
  extends Omit<IReportParams, 'reportType' | 'description'> {
  onClick?(): void
  onCancel?(): void
  articleType?: number
  wrap?: boolean
}

const reportConfig: Record<
  IReportButtonProps['objectType'],
  {
    text: string
    value?: number
    isCustom?: boolean
    isLink?: boolean
  }[]
> = {
  5: [
    { text: '内容不真实', value: 301 },
    { text: '旧闻重复', value: 303 },
    { text: '内容质量差', value: 318 },
    { text: '软文广告', value: 302 },
    { text: '其他问题，我要吐槽', value: 315, isCustom: true },
    { text: '侵权投诉', isLink: true },
  ],
  1: [
    { text: '内容不真实', value: 301 },
    { text: '软文广告', value: 302 },
    { text: '低俗色情', value: 304 },
    { text: '恶意对比', value: 305 },
    { text: '违法信息', value: 307 },
    { text: '没有讨论价值', value: 309 },
    { text: '恶意攻击谩骂', value: 312 },
  ],
  11: [],
}

/*
224：懂车帝短图文
225：懂车帝小视频
226：懂车帝回答转发文
227：懂车帝口碑
228：懂车帝长贴
229：懂车帝提问
230：懂车帝转发
231：懂车帝长图文
232：懂车帝长视频
233：懂车帝图集
*/

const articleTypeSourceMap = makeMap<number>([
  ['1,3,5,7', 224],
  ['6', 225],
  ['12,13,14', 227],
  ['9', 228],
  ['8', 229],
  ['11', 230],
  ['2', 232],
  ['4', 233],
])

export const ReportButton: React.FC<IReportButtonProps> = ({
  onClick,
  onCancel,
  objectId,
  objectType,
  articleType,
  authorId,
  wrap,
}) => {
  const [visible, setVisible] = useState(false)
  const [reportType, setReportType] = useState(0)
  const [inputVisible, setInputVisible] = useState(false)
  const [description, setDescription] = useState('')
  const reportedRef = useRef(false)

  const handleClick = useLoginCheckCallback(
    useCallback(() => {
      setVisible((prev) => !prev)
      if (onClick) {
        onClick()
      }
    }, [onClick]),
    useMemo(
      () => ({
        enter_method: 'other',
      }),
      []
    )
  )

  const { state } = useContext(GlobalStoreContext)

  const handleSubmit = () => {
    if (!reportType) return
    report({
      objectId,
      objectType,
      authorId,
      reportType,
      source: articleTypeSourceMap[articleType!] || 224,
      description,
      userId: state.userData?.user_id,
    })
      .then(() => {
        Toast.show({
          text: '举报成功',
        })
        unstable_batchedUpdates(() => {
          setVisible(false)
          setReportType(0)
          setInputVisible(false)
          setDescription('')
        })
      })
      .catch((e) => {
        Toast.show({
          text: '举报失败',
        })
      })
  }

  // const handleReport = useCallback(
  //   ({ type, data = [] }: IReportData) => {
  //     if (type === 'confirm') {
  //       if (id) {
  //         const reportTypes: string[] = []
  //         const description: string[] = []
  //
  //         data.forEach((item) => {
  //           reportTypes.push(item.value)
  //           if (item.description) {
  //             description.push(item.description)
  //           }
  //         })
  //
  //         reportUser(
  //           id,
  //           reportTypes.join(','),
  //           description.join(''),
  //           userId,
  //           state?.userData?.user_id
  //         )
  //       }
  //       setVisible(false)
  //     } else {
  //       setVisible(false)
  //       if (onCancel && visible) onCancel()
  //     }
  //   },
  //   [id, userId, onCancel, visible]
  // )

  return (
    <React.Fragment>
      <VerticalButton icon="icon_report_24" onClick={handleClick} wrap={wrap}>
        举报
      </VerticalButton>
      {/*<ModalReport visible={visible} onButtonClick={handleReport} />*/}
      <Dialog
        visible={visible}
        onClose={() => {
          setVisible(false)
          if (!reportedRef.current && onCancel) {
            onCancel()
          }
        }}
        className={dialogStyle['space-medium']}
        headerClass={dialogStyle['space-medium']}
      >
        <div className="tw-w-390 tw-px-24">
          <div className="tw-text-20 tw-font-medium">
            {`举报${{ 1: '评论', 5: '内容', 11: '用户' }[objectType]}`}
          </div>
          <div className="tw-grid tw-grid-cols-2 tw-mt-24 tw-gap-y-24 tw-gap-x-8">
            {reportConfig[objectType]?.map((item) => {
              return (
                <div
                  key={item.text}
                  className="tw-flex tw-h-18 tw-items-center tw-cursor-pointer"
                  onClick={() => {
                    setInputVisible(item.isCustom!)
                    if (!item.isCustom) {
                      setDescription('')
                    }
                    if (item.isLink) {
                      window.open('https://www.toutiao.com/complain/')
                    } else {
                      setReportType(item.value!)
                    }
                  }}
                >
                  <div
                    className={classNames(
                      'tw-w-18 tw-h-18 tw-rounded-full tw-inline-flex tw-items-center tw-justify-center tw-mr-4',
                      {
                        'tw-border-1 tw-border-solid tw-border-video-shallow-gray':
                          reportType !== item.value && !item.isLink,
                      }
                    )}
                  >
                    {reportType === item.value ? (
                      <i className="DCD_Icon icon_success tw-text-common-yellow tw-text-18" />
                    ) : null}
                  </div>
                  {item.text}
                  {item.isLink ? (
                    <i className="DCD_Icon icon_triangle_right" />
                  ) : null}
                </div>
              )
            })}
          </div>
          {inputVisible ? (
            <textarea
              className="tw-h-160 tw-bg-common-white tw-rounded-2 tw-px-16 tw-py-12 tw-w-full tw-outline-none tw-mt-24 tw-resize-none"
              placeholder="请描述您反馈的内容或遇到的问题"
              onInput={(e) => {
                setDescription(e.currentTarget.value)
              }}
            />
          ) : null}
          <div className="tw-grid tw-grid-cols-2 tw-gap-8 tw-mt-28 tw-mb-16">
            <button
              type="button"
              className="tw-text-16 tw-font-medium tw-h-44 tw-rounded-2 tw-border-1 tw-border-solid tw-border-video-shallow-gray hover:tw-border-common-yellow hover:tw-bg-common-yellow hover:tw-bg-opacity-24"
              onClick={() => {
                setVisible(false)
              }}
            >
              取消
            </button>
            <button
              type="button"
              className="tw-text-16 tw-font-medium tw-h-44 tw-rounded-2 tw-bg-common-yellow hover:tw-bg-common-highlight-yellow"
              onClick={handleSubmit}
            >
              提交
            </button>
          </div>
        </div>
      </Dialog>
    </React.Fragment>
  )
}

interface IDelCommentButtonProps {
  type?: 'comment' | 'reply' // 评论 / 回复
  id?: string
  onClick?(): void
  onSuccess?(): void // 删除成功
  onError?(): void // 删除失败
  className?: string
}

// 删除 评论 / 回复
export const DelCommentButton: React.FC<IDelCommentButtonProps> = ({
  type,
  id,
  onClick,
  onSuccess,
  onError,
  className,
}) => {
  const [confirmVisible, setConfirmVisible] = useState(false)

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick()
    }
    setConfirmVisible(true)
  }, [onClick])

  const handleConfirm = useCallback(() => {
    const address =
      type === 'reply'
        ? '/motor/proxy/delete_comment/browser/2/comment/v1/delete_comment/'
        : '/motor/proxy/delete_reply/browser/2/comment/v1/delete_reply/'
    fetch(
      getUrl(
        `${address}?${serializeObject({
          aid,
          app_name: appName,
          id,
          reply_id: id,
        })}`
      ),
      {
        headers: {
          'tt-anti-token': window.localStorage.getItem('tt-anti-token') || '',
        },
      }
    )
      .then((data) => {
        const { errno } = data || {}
        if (errno === 0) {
          Toast.show({
            text: '删除评论成功！',
          })
          console.log(onSuccess)
          if (onSuccess) {
            onSuccess()
          }
        } else {
          Toast.show({
            text: '删除评论失败！',
          })
          if (onError) {
            onError()
          }
        }
      })
      .catch((e) => {
        if (onError) {
          onError()
        }
      })
    setConfirmVisible(false)
  }, [type, id, onSuccess, onError])

  return (
    <React.Fragment>
      <VerticalButton
        className={className}
        icon="icon_delete_24"
        onClick={handleClick}
      >
        删除
      </VerticalButton>
      <Dialog
        title={'提示'}
        visible={confirmVisible}
        className={dialogStyle['space-medium']}
        headerClass={dialogStyle['space-medium']}
        onClose={() => setConfirmVisible(false)}
      >
        <Confirm
          onCancel={() => setConfirmVisible(false)}
          onConfirm={handleConfirm}
        >
          确认删除吗？
        </Confirm>
      </Dialog>
    </React.Fragment>
  )
}

// 类型是否可收藏
export const isCollectable = (articleType: number) => {
  return ![12, 13, 14].includes(articleType)
}

// 类型是否可反对
export const isBuryable = (articleType: number) => {
  return [12, 13, 14].includes(articleType)
}

export interface IProps {
  id?: string // 主id 组件用于评论就传评论id 用于文章就传文章id
  diggType?: IToggleDiggParams['type']
  isOwnerDigg?: boolean // 圈主点赞
  questionStatus?: number // 答案状态
  questionDisplay?: string // 答案状态显示文字
  time?: number // 时间
  timeAppend?: string // 时间描述文字
  timePrepend?: string // 时间描述文字
  awardText?: string // 悬赏文字
  awardIcon?: string // 悬赏图标
  commentVisible?: boolean // 显示评论按钮
  commentCount?: number // 评论数
  diggVisible?: boolean // 显示点赞按钮
  diggCount?: IToggleDiggParams['diggCount'] // 点赞数
  selfHasDigg?: IToggleDiggParams['hasDigg'] // 当前用户是否点赞
  groupId?: IToggleDiggParams['groupId'] // 文章id 用于对评论点赞
  commentId?: IToggleDiggParams['commentId'] // 评论id 用于对回复点赞
  cellStyle?: IToggleDiggParams['cellStyle'] // 点赞类型
  articleType?: IToggleDiggParams['articleType'] // 文章类型
  commentLinkWrapper?: Wrapper // 评论跳转链接
  commentText?: string // 评论按钮文字
  collectVisible?: boolean // 显示收藏按钮
  collected?: ICollectButtonProps['collected'] // 是否收藏
  communityId?: string // 车友圈id
  communityName?: string // 车友圈名称
  moreContent?: ReactNode
  buryVisible?: boolean
  buryCount?: IToggleBuryParams['buryCount'] // 反对数
  buried?: IToggleBuryParams['buried'] // 是否已反对
  shareContent?: ToolTipProps['content']
  className?: string
  onClickComment?(event: MouseEvent<HTMLButtonElement>): void
  onClickCollect?(curStatus: boolean): void
  onClickCommunity?(): void
  onClickDigg?(curStatus: boolean): void
  onClickBury?(curStatus: boolean): void
  onShowShare?(): void
}

const Interact: React.FC<IProps> = ({
  id,
  diggType,
  isOwnerDigg,
  questionStatus,
  questionDisplay,
  awardText,
  awardIcon,
  time,
  timeAppend,
  timePrepend,
  commentVisible = true,
  commentCount,
  diggVisible = true,
  diggCount,
  selfHasDigg,
  groupId,
  commentId,
  cellStyle,
  articleType,
  commentText = '评论',
  collectVisible = true,
  collected,
  communityId,
  communityName,
  moreContent,
  buryVisible,
  buryCount,
  buried,
  shareContent,
  className = 'tw-mt-12',
  commentLinkWrapper = noopWrapper,
  onClickComment,
  onClickCollect,
  onClickCommunity,
  onClickDigg,
  onClickBury,
  onShowShare,
}) => {
  const handleClickComment = stopPropagation(onClickComment)
  const handleClickCommunity = stopPropagation(onClickCommunity)

  return (
    <React.Fragment>
      <div className={classNames('tw-flex', className)}>
        <div
          className={classNames(
            'tw-flex tw-items-center tw-flex-1 tw-overflow-hidden tw-mr-24',
            size14Class
          )}
        >
          {communityName ? (
            <InteractButton
              className="tw-mr-20 hover:tw-text-common-orange"
              text={communityName}
              icon={require('static/cheyouquan_icon.png')}
              linkWrapper={communityLink({ id: communityId })}
              onClick={handleClickCommunity}
            />
          ) : null}
          {isOwnerDigg ? (
            <span className="tw-mr-8 tw-text-12 tw-w-56 tw-h-20 tw-text-center tw-leading-20 tw-bg-video-light-gray tw-flex-none">
              圈主点赞
            </span>
          ) : null}
          {time ? (
            <span className="tw-text-video-shallow-gray tw-flex-none">
              {timePrepend}
              {formatTime(time * 1000)}
              {timeAppend}
            </span>
          ) : null}
        </div>
        <div
          className={classNames(
            'right tw-flex tw-items-center tw-flex-none',
            size14Class
          )}
        >
          {questionStatus === 0 && awardText ? (
            <InteractButton
              className="tw-cursor-auto"
              text={awardText}
              icon={awardIcon}
            />
          ) : null}
          {commentVisible ? (
            <InteractButton
              icon={replyIcon}
              hoverIcon={replyIconHover}
              onClick={handleClickComment}
              text={commentText}
              count={commentCount}
              linkWrapper={commentLinkWrapper}
              className="hover:tw-text-common-orange"
            />
          ) : null}
          <DiggBuryProvider>
            {diggVisible ? (
              <DiggButton
                id={id}
                groupId={groupId}
                commentId={commentId}
                diggCount={diggCount}
                hasDigg={selfHasDigg}
                cellStyle={cellStyle}
                articleType={articleType}
                type={diggType}
                onClick={onClickDigg}
                className="hover:tw-text-common-orange"
              />
            ) : null}
            {buryVisible ? (
              <BuryButton
                onClick={onClickBury}
                text={'反对'}
                id={id}
                buryCount={buryCount}
                buried={buried}
                articleType={articleType}
                className="hover:tw-text-common-orange"
              />
            ) : null}
          </DiggBuryProvider>
          {collectVisible ? (
            <CollectButton
              id={id}
              collected={collected}
              onClick={onClickCollect}
              renderText={(status) => (status ? '已收藏' : '收藏')}
              className="hover:tw-text-common-orange"
            />
          ) : null}
          {shareContent ? (
            <ToolTip
              className="tw-inline-flex tw-items-center"
              theme={'light'}
              content={shareContent}
              onTipShow={onShowShare}
            >
              {(showTips) => (
                <InteractButton
                  icon={showTips ? shareIconHover : shareIcon}
                  hoverIcon={shareIconHover}
                  text={'分享'}
                  className={classNames('hover:tw-text-common-orange', {
                    'tw-text-common-orange': showTips,
                  })}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                />
              )}
            </ToolTip>
          ) : null}
          {questionStatus && questionDisplay ? (
            <InteractButton
              className="tw-text-common-green tw-cursor-auto"
              text={questionDisplay}
            />
          ) : null}
          {moreContent ? (
            <ToolTip tag={'button'} theme={'light'} content={moreContent}>
              {(showTips) => (
                <i
                  className={classNames(
                    'DCD_Icon icon_more_24 hover:tw-text-common-orange',
                    {
                      'tw-text-common-orange': showTips,
                    }
                  )}
                />
              )}
            </ToolTip>
          ) : null}
        </div>
      </div>
      <style jsx>
        {`
          .community {
            background: url(${require('static/cheyouquan_icon.png')}) left
              center / 16px 16px no-repeat;
          }
          .right > :global(* + *) {
            margin-left: 24px;
          }
        `}
      </style>
    </React.Fragment>
  )
}

interface IInteractVideoProps {
  moreContent: ReactNode
  shareContent?: ToolTipProps['content']
  comment_count: number
  digg_count: number
  is_digg: boolean
  is_collected: boolean
  onClickComment?(): void //点击评论
  onClickCollect?(curStatus: boolean): void //点击收藏
  onClickDigg?(curStatus: boolean): void //点击点赞
}
export const InteractVideo: React.FC<IInteractVideoProps> = ({
  moreContent,
  shareContent,
  comment_count = 0,
  digg_count = 0,
  is_digg,
  is_collected,
  onClickComment, //点击评论
  onClickCollect, //点击收藏
  onClickDigg, //点击点赞
}) => {
  const list = [
    {
      count: comment_count,
      fn: onClickComment,
      status: true,
      text: '评论',
      classname: 'icon_comment_24',
    },
    {
      count: digg_count,
      fn: onClickDigg,
      status: is_digg,
      text: '赞同',
      classname: is_digg
        ? 'icon_like_select_pc tw-text-common-yellow'
        : 'icon_like_24_normal',
    },
    {
      fn: onClickCollect,
      status: is_collected,
      text: is_collected ? '已收藏' : '收藏',
      classname: is_collected
        ? 'icon_collect_select_pc tw-text-common-yellow'
        : 'icon_collect_24_normal',
    },
  ]
  return (
    <React.Fragment>
      <div className={classNames('tw-flex tw-items-center')}>
        {list.map((item: any) => {
          return (
            <div
              className="  tw-cursor-pointer hover:tw-text-common-orange tw-text-video-gery tw-text-14 tw-mr-25"
              onClick={() => item?.fn(item?.status)}
              key={item?.text}
              style={{ display: 'block' }}
            >
              <i className={`DCD_Icon ${item?.classname} tw-text-14 tw-mr-3`} />
              {item?.count ? formatNumber(item?.count) : ''}
              {item?.text}
            </div>
          )
        })}
        {shareContent ? (
          <ToolTip
            className=" tw-cursor-pointer hover:tw-text-common-orange tw-text-video-gery tw-text-14 tw-mr-25"
            theme={'light'}
            content={shareContent}
          >
            <i className="DCD_Icon icon_share_24 tw-text-14" />
            分享
          </ToolTip>
        ) : null}
        {moreContent ? (
          <ToolTip tag={'button'} theme={'light'} content={moreContent}>
            {(showTips) => (
              <i
                className={classNames(
                  'DCD_Icon icon_more_24 hover:tw-text-common-orange',
                  {
                    'tw-text-common-orange': showTips,
                  }
                )}
              />
            )}
          </ToolTip>
        ) : null}
      </div>
    </React.Fragment>
  )
}

export default Interact
